import React, { createContext, useContext, useMemo } from 'react'

import { useKnowledgeDocumentDataContext } from './KnowledgeDocumentDataContext'
import { useKnowledgeDocumentUXContext } from './KnowledgeDocumentUXContext'

export interface DocumentAgentState {
  siteId: string
  orgId: string
  openDocumentIds: string[]
  activeTabType: 'search' | 'document'
  activeDocumentId: string | null
}

interface DocumentAgentContextType {
  agentState: DocumentAgentState
}

const DocumentAgentContext = createContext<
  DocumentAgentContextType | undefined
>(undefined)

export const DocumentAgentProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { siteID, orgID } = useKnowledgeDocumentDataContext()
  const { openTabs, isOnDocument, currentDocumentId } =
    useKnowledgeDocumentUXContext()

  const agentState = useMemo<DocumentAgentState>(() => {
    const openDocumentIds: string[] = []

    for (const tab of openTabs) {
      const currentHistoryItem = tab.history[tab.currentIndex]
      if (currentHistoryItem.type === 'document') {
        openDocumentIds.push(currentHistoryItem.documentId)
      }
    }

    return {
      siteId: siteID,
      orgId: orgID,
      openDocumentIds: [...new Set(openDocumentIds)],
      activeTabType: isOnDocument ? 'document' : 'search',
      activeDocumentId: isOnDocument ? currentDocumentId : null,
    }
  }, [siteID, orgID, openTabs, isOnDocument, currentDocumentId])

  const value = useMemo(() => ({ agentState }), [agentState])

  return (
    <DocumentAgentContext.Provider value={value}>
      {children}
    </DocumentAgentContext.Provider>
  )
}

export const useDocumentAgentContext = (): DocumentAgentContextType => {
  const context = useContext(DocumentAgentContext)
  if (context === undefined) {
    throw new Error(
      'useDocumentAgentContext must be used within a DocumentAgentProvider'
    )
  }
  return context
}

export const useDocumentAgentState = (): DocumentAgentState => {
  const { agentState } = useDocumentAgentContext()
  return agentState
}

/**
 * Returns the document agent state if inside a DocumentAgentProvider, otherwise undefined.
 * Use this when the chatbot may or may not be rendered in a document viewer context.
 */
export const useDocumentAgentStateOptional = ():
  | DocumentAgentState
  | undefined => {
  const context = useContext(DocumentAgentContext)
  return context?.agentState
}
